import get from 'lodash/get';
import set from 'lodash/set';
import uniq from 'lodash/uniq';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import { handleActions } from 'redux-actions';
import extend from 'extend';
import { type SelectAnswerPayload, type RemoveAnswerPayload, type UpdateAnswersPayload, type UpdateNotePayload, type Action } from '../actions/index';
import { getAllDescendentFollowUpQuestionIds, getFollowUpQuestionIdsByAnswers } from './selectors';
import type { QnAStateType } from './index';
export type UserAnswersT = Array<string | File>;
export type UserAnswerItemT = {
  question: string;
  answers: UserAnswersT;
  note?: string;
  noteDesc?: string;
  noteValidator?: string;
  tags?: Array<string>;
};
export type UserAnswerMapT = {
  [key: string]: UserAnswerItemT;
};
export const defaultState: UserAnswerMapT = {
  'cebf8f04-d650-4f74-a2a5-77038433e731': {
    question: 'Have you finish your breakfast?',
    answers: ['Yes'],
    note: ''
  }
};
export const selectAnswer = (state: UserAnswerMapT, {
  questionId,
  question,
  answer,
  limit,
  type,
  qnaState
}: SelectAnswerPayload) => {
  let nextAnswers = uniq([answer, ...(get(state, [questionId, 'answers']) || [])]);
  if (type === 'input' || type === 'number' || type === 'richText') {
    nextAnswers = nextAnswers.slice(0, 1);
  } else if ((limit === 0 || limit) && limit !== -1) {
    nextAnswers = nextAnswers.slice(0, limit);
  }
  let nextState = extend(true, {}, state, {
    [questionId]: {
      question,
      answers: nextAnswers
    }
  });
  nextState = removeStaleFollowUps(questionId, qnaState, nextState);
  return nextState;
};
export const removeAnswer = (state: UserAnswerMapT, {
  questionId,
  answer,
  qnaState
}: RemoveAnswerPayload) => {
  let nextState = cloneDeep(state);
  if (!answer) {
    // remove all
    delete nextState[questionId];
  } else {
    // remove selected
    set(nextState, [questionId, 'answers'], get(state, [questionId, 'answers'], []).filter(userAnswer => userAnswer !== answer));
  }
  nextState = removeStaleFollowUps(questionId, qnaState, nextState);
  return nextState;
};
export const updateAnswers = (state: UserAnswerMapT, {
  questionId,
  answers
}: UpdateAnswersPayload) => {
  let nextState = cloneDeep(state);
  set(nextState, [questionId, 'answers'], answers);
  return nextState;
};
export const updateNote = (state: UserAnswerMapT, {
  questionId,
  note
}: UpdateNotePayload) => {
  const finalState = extend(true, {}, state, {
    [questionId]: {
      note
    }
  });
  return finalState;
};

// remove answers of followup questions that are not applicable.
export function removeStaleFollowUps(questionId: string, state: QnAStateType, newAnswers: UserAnswerMapT): UserAnswerMapT {
  const currentFollowUps = getFollowUpQuestionIdsByAnswers(state, questionId);
  const newState = {
    ...state,
    answers: newAnswers
  };
  const newFollowUps = getFollowUpQuestionIdsByAnswers(newState, questionId);
  const removeList = difference(currentFollowUps, newFollowUps);
  const listWithNestedQuestions = [...removeList];
  removeList.forEach(id => {
    listWithNestedQuestions.push(...getAllDescendentFollowUpQuestionIds(state, id));
  });
  let resultAnswers = {
    ...newAnswers
  };
  listWithNestedQuestions.forEach(questionId => {
    delete resultAnswers[questionId];
  });
  return resultAnswers;
}
function removeQuestion(state: UserAnswerMapT, action: Action) {
  const questionId = get(action, 'questionId');
  const nextState = {
    ...state
  };
  delete nextState[questionId];
  return nextState;
}
type HandlerFunc = (state: UserAnswerMapT, payload: any) => UserAnswerMapT;
const generateHandlers = (handlers: {
  [key: string]: HandlerFunc;
}) => Object.keys(handlers).reduce((prev, key: string) => {
  prev[key] = (state: UserAnswerMapT, action: Action) => {
    return handlers[key](state, action.payload);
  };
  return prev;
}, {});
export const answerReducers = {
  updateAnswers,
  selectAnswer,
  updateNote,
  removeAnswer,
  removeQuestion
};
export default handleActions<UserAnswerMapT, Action>(generateHandlers({
  updateAnswers,
  selectAnswer,
  updateNote,
  removeAnswer,
  removeQuestion
}), {});